import _ = require('underscore');
import { ITableInfo } from '../models/ITableInfo';
import { IUserInfo } from '../models/IUserInfo';
import {
    ICatalogUserInfo,
    IDataQualityWarningAuthorInfo,
    IDataQualityWarningInfo,
    IDownstreamDatasourcesConnectionInfo,
    IDownstreamWorkbooksConnectionInfo,
    ITableColumnInfo,
    ITableColumnsConnectionInfo,
    ITableDatabaseInfo,
    ITagInfo
} from '../models/resourceInfoParts';
import { IMaxDownstreamDatasources, IMaxDownstreamWorkbooks } from '../ServerApi/types/IMaxDownstreamConnections';
import { ITable } from '../ServerApi/types/ITable';
import { ICatalogUserItem, IDataQualityIndicator, ITableDatabaseItem } from '../ServerApi/types/resourceParts';
import { MapperHelper } from './MapperHelper';

export module TableWithCrassMapper {

    const M = MapperHelper;

    export function map(
        serverApiTables: ITable[],
        maxDownstreamDatasources: IMaxDownstreamDatasources,
        maxDownstreamWorkbooks: IMaxDownstreamWorkbooks,
      ): ITableInfo[] {
          return _.map(serverApiTables, (table: ITable) => {
            const dbInfo: ITableInfo = {
                __typename:                         'DatabaseTable',
                certifier:                          mapCertifier(table.certifier),
                certificationNote:                  table.certificationNote,
                contact:                            mapContact(table.contact),
                contentType:                        'tableWithCrass',
                createdAt:                          M.toDate(table.createdAt),
                dataQualityWarnings:                mapDataQualityWarning(table),
                description:                        table.description,
                downstreamWorkbooksConnection:      mapDownstreamWorkbooks(table),
                downstreamDatasourcesConnection:    mapDownstreamDatasources(table),
                maxDownstreamDatasources:           maxDownstreamDatasources ? maxDownstreamDatasources.downstreamDatasourcesConnection.totalCount : 0,
                maxDownstreamWorkbooks:             maxDownstreamWorkbooks ? maxDownstreamWorkbooks.downstreamWorkbooksConnection.totalCount : 0,
                id:                                 table.graphQLId || '',
                isCertified:                        table.isCertified,
                isGroupedParent:                    table.isGroupedParent,
                luid:                               table.luid,
                name:                               table.name,
                site:                               {id: table.siteId, luid: table.siteLuid, urlName: ''},
                tags:                               mapTags(table.tags),
                updatedAt:                          M.toDate(table.updatedAt),
                database:                           mapDatabase(table.database),
                columnsConnection:                  mapColumns(table),
                vizportalId:                        table.id
            };
            return dbInfo;
          });
    }

    function mapTags(tableTags: string[]): ITagInfo[] {
        return tableTags.map(t => {
            const tag: ITagInfo = {
                __typename: 'Tag',
                name: t
            };
            return tag;
        });
    }

    function mapColumns(table: ITable): ITableColumnsConnectionInfo {
        const columns: ITableColumnsConnectionInfo = {
            totalCount: table.columns.length,
            nodes: table.columns.map(column => {
                const node: ITableColumnInfo = {
                    name: column.columnName,
                };
                return node;
            })
        };
        return columns;
    }

    function mapDatabase(tableDatabaseItem: ITableDatabaseItem): ITableDatabaseInfo {
        return tableDatabaseItem ? {
                __typename: tableDatabaseItem.dbType,
                isGrouped: tableDatabaseItem.isGroupedParent,
                hostName: tableDatabaseItem.hostName,
                name: tableDatabaseItem.name,
        } : null;
    }

    function mapDownstreamWorkbooks(table: ITable): IDownstreamWorkbooksConnectionInfo {
        const dsWorkbookConnection: IDownstreamWorkbooksConnectionInfo = {
            totalCount: table.downstreamWorkbookCount
        };
        return dsWorkbookConnection;
    }

    function mapDownstreamDatasources(table: ITable): IDownstreamDatasourcesConnectionInfo {
        const dsDatasourcesConnection: IDownstreamDatasourcesConnectionInfo = {
            totalCount: table.downstreamDataSourceCount
        };
        return dsDatasourcesConnection;
    }

    function mapDataQualityWarningOwnerToAuthor(dataQualityWarningOwner: IUserInfo | null): IDataQualityWarningAuthorInfo {
        const author: IDataQualityWarningAuthorInfo = {
            username: dataQualityWarningOwner?.username || '',
            name: dataQualityWarningOwner?.displayName || '',
            domain: dataQualityWarningOwner?.domainName || '',
            ...dataQualityWarningOwner
        };
        return author;
    }

    function mapDataQualityWarningUpdatedAt(updatedAt: string | null) {
        return updatedAt ? new Date(updatedAt) : null;
    }

    export function mapDataQualityWarning(table: ITable): IDataQualityWarningInfo[] {
        const indicators: IDataQualityIndicator[] = table.dataQualityWarning;
        return indicators.map(dqw => {
            const dataQualityWarning: IDataQualityWarningInfo = {
                author:            mapDataQualityWarningOwnerToAuthor(dqw.owner),
                owner:             mapDataQualityWarningOwnerToAuthor(dqw.owner),
                authorDisplayName: dqw.owner?.displayName || '',
                isActive:          dqw.isActive,
                isSevere:          dqw.isSevere,
                message:           dqw.message,
                vizportalId:       dqw.id,
                warningType:       dqw.type,
                updatedAt:         mapDataQualityWarningUpdatedAt(dqw.updatedAt),
                assetUrl:          (url: string) => { return table.luid; },
                assetName:         table.name,
                assetType:         table.type
            };

            return dataQualityWarning;
        });
    }

    export function mapContact(contact: ICatalogUserItem): ICatalogUserInfo {
        return contact ? {
            id: contact.id,
            readOnly: contact.readOnly,
            username: contact.username,
            name: contact.displayName,
            domain: contact.domainName,
            hasServerDisplay: contact.hasServerDisplay
        } : null;
    }

    function mapCertifier(certifier: ICatalogUserItem | null): ICatalogUserInfo {
        return certifier ? {
            id: certifier.id,
            name: certifier.displayName,
            username: certifier.username,
            domain: certifier.domainName,
            readOnly: !!certifier.readOnly,
            hasServerDisplay: certifier.hasServerDisplay
        } : null;
    }
}